import { displayInitial } from '../utils/displayInitial'
import { dialog } from '../services/dialog'
import { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense, type ReactNode } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import {
  Users,
  Shield,
  ShieldCheck,
  Ban,
  Plus,
  Trash2,
  Link2,
  Activity,
  Network,
  ChevronRight,
  ChevronDown,
  CornerDownRight,
  Wallet,
  Cog,
} from 'lucide-react'
import api, { departmentApi } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { useServerStore } from '../shell/stores/serverStore'
import { serverBillingApi } from '../services/billing'
import { getGroupedServerSections } from '../shell/admin-registry'
import { type AdminGateCtx } from '../shell/adminGate'
import { DelegatedTab } from '../components/admin/DelegatedTab'
import { PoliciesTab } from '../components/governance/PoliciesTab'
import { mediaUrl } from '../services/config'
import AppLayout from '../components/layout/AppLayout'
import { Pagination, usePaginatedSlice } from '../components/ui/pagination'
import { HeadSelect } from './admin/HeadSelect'
import InvitesTab from './admin/server/InvitesTab'
import { MembersTab } from './admin/server/MembersTab'
import { RolesTab } from './admin/server/RolesTab'
import { ServerSettingsTab } from './admin/server/ServerSettingsTab'
import { AdminShell, type AdminTab } from '../components/admin/AdminShell'
import { KpiCard, KpiRow, SectionCard, TablePanel, DataTable, type Column } from '../components/patterns'

// Money is integer cents; sign-aware so a negative balance renders
// "-$12.50" rather than "$-12.50" (mirrors ServerBillingPanel.fmtUsd).
function fmtUsd(cents: number): string {
  const abs = Math.abs(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `${cents < 0 ? '-' : ''}$${abs}`
}

interface Member {
  id: string
  userId: string
  nickname: string | null
  banned: boolean
  departmentId?: string | null
  department?: { id: string; name: string } | null
  user: {
    id: string
    email: string
    avatarUrl: string | null
    status: string
    firstName?: string
    lastName?: string | null
    position?: string | null
    aboutMe?: string | null
  }
  roles: { role: { id: string; name: string; color: string } }[]
}

interface ServerDepartment {
  id: string
  serverId: string
  name: string
  parentId: string | null
  headUserId: string | null
  reportsToUserId: string | null
  head: { id: string; email: string; firstName?: string; lastName?: string | null; avatarUrl?: string | null } | null
  _count: { members: number; children: number }
  createdAt: string
  updatedAt: string
}

interface Role {
  id: string
  name: string
  color: string
  permissions: string
  position: number
}

interface BanRecord {
  id: string
  userId: string | null
  email: string | null
  reason: string | null
  bannedAt: string
  expiresAt: string | null
}

// PERMISSIONS / hasBit / toggleBit moved to
// ./admin/server/permissionCatalog.ts (with module grouping +
// baseline defaults). The Roles tab consumes them via RolesTab.tsx.

export default function ServerAdminPage() {
  const { serverId } = useParams<{ serverId: string }>()
  const [, setParams] = useSearchParams()
  const [members, setMembers] = useState<Member[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [bans, setBans] = useState<BanRecord[]>([])
  const [invites, setInvites] = useState<any[]>([])
  const [departments, setDepartments] = useState<ServerDepartment[]>([])
  // `loading` only blanks the page on the FIRST load. Every later
  // refetch is triggered by an edit and must run SILENTLY — otherwise
  // setLoading(true) makes renderTab() swap the whole tab for
  // "Loading…", unmounting it and wiping its local state (expanded
  // org tree, selection, inline-edit buffers, form inputs, paging).
  const [loading, setLoading] = useState(false)
  const hasLoadedRef = useRef(false)
  // Telephony/Billing tabs are owner-or-global-admin only (matches the
  // server-side requireServerBillingAdmin guard) — a server-Admin-role
  // member can open this page but must not see tabs whose API 403s.
  const [serverOwnerId, setServerOwnerId] = useState<string | null>(null)
  const currentUser = useAuthStore((s) => s.user)
  const enabledModules = useServerStore((s) => s.servers.find((sv) => sv.id === serverId)?.enabledModules)

  const [banPage, setBanPage] = useState(1)
  const [banSize, setBanSize] = useState(10)

  const fetchData = useCallback(async () => {
    if (!serverId) return
    // Full-screen spinner only before the first dataset arrives.
    if (!hasLoadedRef.current) setLoading(true)
    // Each tab's data is fetched independently and failure-isolated.
    // Previously a single Promise.all meant one 403 (e.g. user-invites
    // is gated tighter than the rest) rejected the whole batch and
    // left EVERY tab — members, roles, bans, departments — empty for
    // a server-Admin-role user who isn't the owner or a global admin.
    // Per-call catch keeps the tabs the user CAN see populated and
    // only the forbidden one empty.
    const [membersRes, rolesRes, bansRes, invitesRes, deptsRes, serverRes] = await Promise.all([
      api.get(`/servers/${serverId}/members`).catch(() => ({ data: [] })),
      api.get(`/servers/${serverId}/roles`).catch(() => ({ data: [] })),
      api.get(`/servers/${serverId}/bans`).catch(() => ({ data: [] })),
      api.get(`/servers/${serverId}/user-invites`).catch(() => ({ data: [] })),
      departmentApi.list(serverId).catch(() => ({ data: [] as ServerDepartment[] })),
      api.get(`/servers/${serverId}`).catch(() => ({ data: null })),
    ])
    setMembers(membersRes.data)
    setRoles(rolesRes.data)
    setBans(bansRes.data)
    setInvites(invitesRes.data)
    setDepartments(deptsRes.data)
    setServerOwnerId(serverRes.data?.ownerId ?? null)
    hasLoadedRef.current = true
    setLoading(false)
  }, [serverId])

  // Silent, departments-only refresh. Used by the Departments tab to
  // reconcile after create/delete/bulk and to roll back a failed
  // optimistic edit — never blanks the page (the tab stays mounted).
  const refreshDepartments = useCallback(async () => {
    if (!serverId) return
    const res = await departmentApi.list(serverId).catch(() => null)
    if (res) setDepartments(res.data)
  }, [serverId])

  // Optimistic single-department patch — applied immediately so inline
  // edits in the Departments tab feel instant (the org tree + chief
  // grouping recompute without waiting for the server round-trip).
  const patchDepartment = useCallback((id: string, partial: Partial<ServerDepartment>) => {
    setDepartments((prev) => prev.map((d) => (d.id === id ? { ...d, ...partial } : d)))
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // updateMemberRoles / kickMember / saveMember / assignDepartment /
  // banUser were inline helpers on the old flat Members table. The
  // new MembersTab component owns those calls (it has selection
  // state and bulk-aware routes). Keep `unban` for the Bans tab.

  const unban = async (banId: string) => {
    // Optimistic remove so the row vanishes instantly; restore + alert
    // if the server rejects it.
    const prev = bans
    setBans((b) => b.filter((x) => x.id !== banId))
    try {
      await api.delete(`/servers/${serverId}/bans/${banId}`)
    } catch {
      setBans(prev)
      void dialog.alert({ message: 'Failed to unban', tone: 'destructive' })
    }
  }

  // createRole / deleteRole / updateRole moved into RolesTab.tsx
  // (it owns its own fetchRoles + optimistic update flow).

  // "Active" = not used-up AND not expired (multi-use aware: an invite
  // with uses < maxUses is still active even though usedAt may be set).
  const pendingInvites = useMemo(
    () => invites.filter((i) => {
      const exhausted = i.maxUses != null && (i.uses ?? 0) >= i.maxUses
      const expired = i.expiresAt && new Date(i.expiresAt) < new Date()
      return !exhausted && !expired
    }).length,
    [invites],
  )

  const pagedBans = usePaginatedSlice(bans, banPage, banSize)

  const canBilling =
    !!currentUser &&
    (!!currentUser.isAdmin || currentUser.role === 'ADMIN' || (serverOwnerId != null && currentUser.id === serverOwnerId))

  // Gate context for the admin-section registry (shell/admin-registry).
  const gateCtx: AdminGateCtx = {
    isGlobalAdmin: !!currentUser && (!!currentUser.isAdmin || currentUser.role === 'ADMIN'),
    isServerOwner: serverOwnerId != null && currentUser?.id === serverOwnerId,
    // Server admin (employer) — owner, a MANAGE_SERVER/ADMINISTRATOR role (via
    // the server-computed canManagePeople flag), or a global admin. Gates
    // Class-O oversight surfaces (Monitoring). See access-privacy spec P3.
    isServerAdmin:
      !!currentUser &&
      (!!currentUser.isAdmin ||
        currentUser.role === 'ADMIN' ||
        (serverOwnerId != null && currentUser.id === serverOwnerId) ||
        !!currentUser.canManagePeople),
    enabledModules: enabledModules ?? null,
  }
  // Delegated + module server-admin sections, memoized so the lazy components
  // are stable across the page's frequent silent refetches.
  const grouped = useMemo(
    () => getGroupedServerSections(gateCtx),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gateCtx.isGlobalAdmin, gateCtx.isServerOwner, gateCtx.isServerAdmin, enabledModules],
  )
  const allSections = useMemo(() => [...grouped.modules, ...grouped.platform], [grouped])
  const sectionLazies = useMemo(
    () => new Map(allSections.map((s) => [s.id, lazy(s.component)])),
    [allSections],
  )

  // Overview balance card — same audience as the Billing tab (the
  // canBilling dep re-runs the fetch once ownerId resolves). Silent on
  // ANY error: 403/404/network simply renders no card.
  const [balanceCents, setBalanceCents] = useState<number | null>(null)
  useEffect(() => {
    setBalanceCents(null)
    if (!serverId || !canBilling) return
    let cancelled = false
    serverBillingApi(serverId).summary()
      .then((s) => { if (!cancelled) setBalanceCents(s.balanceCents) })
      .catch(() => { /* no card */ })
    return () => { cancelled = true }
  }, [serverId, canBilling])

  const localTabs: AdminTab[] = [
    { id: 'overview',    label: 'Overview',    icon: <Activity size={16} />, subtitle: 'Server snapshot' },
    { id: 'members',     label: 'Members',     icon: <Users size={16} />,    count: members.length,     subtitle: 'Manage who is in this server' },
    { id: 'roles',       label: 'Roles',       icon: <Shield size={16} />,   count: roles.length,       subtitle: 'Permissions and access groups' },
    { id: 'departments', label: 'Departments', icon: <Network size={16} />,  count: departments.length, subtitle: 'Org chart - departments, managers, hierarchy' },
    { id: 'bans',        label: 'Bans',        icon: <Ban size={16} />,      count: bans.length,        subtitle: 'Blocked users' },
    { id: 'invites',     label: 'Invites',     icon: <Link2 size={16} />,    count: pendingInvites,     subtitle: 'Registration links' },
    { id: 'policies',    label: 'Policies',    icon: <ShieldCheck size={16} />,                          subtitle: 'Communication governance — AI watch rules, alerts, barriers' },
    { id: 'settings',    label: 'Settings',    icon: <Cog size={16} />,                                  subtitle: 'Server configuration' },
  ]
  const toTab = (s: typeof grouped.modules[number], group: string): AdminTab => {
    const Ico = s.icon
    return { id: s.id, label: s.label, subtitle: s.subtitle, group, icon: Ico ? <Ico size={16} /> : undefined }
  }
  const moduleTabs: AdminTab[] = grouped.modules.map((s) => toTab(s, 'Modules'))
  const platformTabs: AdminTab[] = grouped.platform.map((s) => toTab(s, 'Platform'))
  const ADMIN_TABS: AdminTab[] = [...localTabs, ...moduleTabs, ...platformTabs]

  const banColumns: Column<BanRecord>[] = [
    { key: 'user', header: 'User / Email', render: (b) => <span className="text-foreground">{b.userId || b.email || 'Unknown'}</span> },
    { key: 'reason', header: 'Reason', render: (b) => <span className="text-muted-foreground">{b.reason || '-'}</span> },
    { key: 'bannedAt', header: 'Banned at', render: (b) => <span className="text-muted-foreground tabular-nums">{new Date(b.bannedAt).toLocaleDateString()}</span> },
    {
      key: 'actions', header: '', align: 'right',
      render: (b) => (
        <button onClick={() => unban(b.id)} className="px-3 py-1 text-xs bg-green-500/20 text-green-500 hover:bg-green-600/30 rounded transition">Unban</button>
      ),
    },
  ]

  if (!serverId) {
    return (
      <AppLayout>
        <div className="flex-1 flex items-center justify-center bg-background">
          <div className="text-destructive text-lg">Server not found</div>
        </div>
      </AppLayout>
    )
  }

  return (
    <AdminShell title="Server admin" icon={<Shield size={16} />} tabs={ADMIN_TABS}>
      {(active) => {
        if (loading) {
          return <div className="min-h-0 flex-1 overflow-y-auto"><div className="py-12 text-center text-muted-foreground">Loading...</div></div>
        }

        if (active === 'overview') {
          return (
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="p-6 space-y-6">
                {canBilling && balanceCents !== null && (
                  <button
                    type="button"
                    onClick={() =>
                      setParams(
                        (prev) => {
                          const next = new URLSearchParams(prev)
                          next.set('tab', 'billing')
                          return next
                        },
                        { replace: false },
                      )
                    }
                    className="w-full text-left"
                    title="Open Billing"
                  >
                    <KpiCard
                      label="Balance"
                      value={fmtUsd(balanceCents)}
                      accent={balanceCents < 0 ? 'text-destructive' : 'text-foreground'}
                      icon={<Wallet size={18} />}
                      suffix={
                        <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-primary/15 text-primary">
                          Top up
                        </span>
                      }
                      className="transition hover:ring-primary/50"
                    />
                  </button>
                )}
                <KpiRow>
                  <KpiCard label="Members" value={members.length} accent="text-blue-500" icon={<Users size={18} />} />
                  <KpiCard label="Roles" value={roles.length} accent="text-emerald-500" icon={<Shield size={18} />} />
                  <KpiCard label="Pending invites" value={pendingInvites} accent="text-amber-500" icon={<Link2 size={18} />} />
                  <KpiCard label="Bans" value={bans.length} accent="text-rose-500" icon={<Ban size={18} />} />
                </KpiRow>
                <SectionCard icon={<Users size={14} />} title="Recent members" subtitle="Last 5">
                  <div className="space-y-1">
                    {members.filter((m) => m.user).slice(0, 5).map((m) => (
                      <div key={m.id} className="w-full flex items-center gap-3 px-3 py-2 rounded text-left">
                        <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white text-[10px] font-bold overflow-hidden">
                          {m.user.avatarUrl ? <img src={mediaUrl(m.user.avatarUrl)} alt="" className="w-full h-full object-cover" /> : displayInitial(m.user.firstName || m.user.email)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-foreground truncate">{(m.user.firstName || m.user.lastName) ? `${m.user.firstName || ''} ${m.user.lastName || ''}`.trim() : m.user.email || '-'}</div>
                          <div className="text-xs text-muted-foreground truncate">{m.user.email || '-'}</div>
                        </div>
                        <span className="text-xs text-muted-foreground">{m.user.position || '-'}</span>
                      </div>
                    ))}
                    {members.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No members yet</div>}
                  </div>
                </SectionCard>
              </div>
            </div>
          )
        }

        if (active === 'members') {
          return <div className="flex min-h-0 flex-1 flex-col p-6"><MembersTab serverId={serverId} departments={departments} roles={roles} /></div>
        }

        if (active === 'roles') {
          return <DelegatedTab icon={<Shield size={14} />} title="Roles & permissions" subtitle="Permission groups for this server"><RolesTab serverId={serverId} /></DelegatedTab>
        }

        if (active === 'bans') {
          return (
            <div className="flex min-h-0 flex-1 flex-col p-6">
              <TablePanel
                icon={<Ban size={16} />}
                title="Bans"
                count={bans.length}
                subtitle="Users blocked from rejoining"
                className="min-h-0 flex-1"
                footer={bans.length > 0 ? (
                  <Pagination total={bans.length} page={banPage} pageSize={banSize} onPageChange={setBanPage} onPageSizeChange={(s) => { setBanSize(s); setBanPage(1) }} />
                ) : undefined}
              >
                <DataTable columns={banColumns} rows={pagedBans} rowKey={(b) => b.id} empty={<div className="py-12 text-center text-sm text-muted-foreground">No active bans</div>} />
              </TablePanel>
            </div>
          )
        }

        if (active === 'invites') {
          return (
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="p-6">
                <InvitesTab serverId={serverId} invites={invites} departments={departments} onChanged={fetchData} />
              </div>
            </div>
          )
        }

        if (active === 'settings') {
          return <ServerSettingsTab serverId={serverId} />
        }

        if (active === 'departments') {
          return (
            <DelegatedTab icon={<Network size={14} />} title="Departments" subtitle="Org chart for this server - every member's reporting line walks up to the root">
              <DepartmentsTab serverId={serverId} departments={departments} members={members} onPatch={patchDepartment} onRefresh={refreshDepartments} />
            </DelegatedTab>
          )
        }

        if (active === 'policies') {
          return (
            <DelegatedTab icon={<ShieldCheck size={14} />} title="Policies" subtitle="Communication governance for this server">
              <PoliciesTab serverId={serverId} />
            </DelegatedTab>
          )
        }

        const Lazy = sectionLazies.get(active)
        if (Lazy) {
          return (
            <Suspense
              fallback={<div className="min-h-0 flex-1 overflow-y-auto"><div className="py-12 text-center text-muted-foreground">Loading...</div></div>}
            >
              <Lazy serverId={serverId} />
            </Suspense>
          )
        }

        return null
      }}
    </AdminShell>
  )
}

// MemberEditModal deleted 2026-05-28. Was a 4-field copy of the
// PeopleEditModal (apps/web/src/components/people/PeopleEditModal.tsx)
// missing email / cellPhone / hireDate / locationCountry — and any
// future field added to the canonical modal had to be added twice.
// Members tab now imports PeopleEditModal via MembersTab.tsx. If you
// need to extend the per-member edit form, edit PeopleEditModal.

/* ─── Departments tab (per-server org chart, OSPROD-79) ─── */

/** OSPROD-265: flatten the department tree in DFS order for <select> options,
 *  indenting each level with NBSPs so parent pickers read as a hierarchy
 *  ("Rx4Route Development" → "   Rx4Route Support") instead of a flat list.
 *  Departments whose parentId points at an id not in the list (soft-deleted
 *  parent) are treated as roots so they never disappear from pickers. */
function deptTreeOptions(departments: ServerDepartment[]): { id: string; label: string; depth: number }[] {
  const known = new Set(departments.map((d) => d.id))
  const byParent = new Map<string | null, ServerDepartment[]>()
  for (const d of departments) {
    const key = d.parentId && known.has(d.parentId) ? d.parentId : null
    if (!byParent.has(key)) byParent.set(key, [])
    byParent.get(key)!.push(d)
  }
  for (const arr of byParent.values()) arr.sort((a, b) => a.name.localeCompare(b.name))
  const out: { id: string; label: string; depth: number }[] = []
  const walk = (parentId: string | null, depth: number) => {
    if (depth > 64) return // cycle guard — server enforces acyclicity, belt+braces here
    for (const d of byParent.get(parentId) ?? []) {
      out.push({ id: d.id, label: `${'   '.repeat(depth)}${d.name}`, depth })
      walk(d.id, depth + 1)
    }
  }
  walk(null, 0)
  return out
}

function DepartmentsTab({
  serverId,
  departments,
  members,
  onPatch,
  onRefresh,
}: {
  serverId: string
  departments: ServerDepartment[]
  members: Member[]
  onPatch: (id: string, partial: Partial<ServerDepartment>) => void
  onRefresh: () => Promise<void> | void
}) {
  const navigate = useNavigate()
  const [newName, setNewName] = useState('')
  const [newParent, setNewParent] = useState<string>('')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  // Bulk selection — checkbox per row + an action bar to move the
  // selected departments under a new parent or delete them in one go.
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkParent, setBulkParent] = useState<string>('')
  const [bulkBusy, setBulkBusy] = useState(false)

  const byParent = (() => {
    const m = new Map<string | null, ServerDepartment[]>()
    // OSPROD-265: a dept whose parentId points at a missing/soft-deleted
    // department used to render NOWHERE (not top-level, not reachable as a
    // child) — treat unknown parents as root so no department can vanish.
    const known = new Set(departments.map((d) => d.id))
    for (const r of departments) {
      const key = r.parentId && known.has(r.parentId) ? r.parentId : null
      if (!m.has(key)) m.set(key, [])
      m.get(key)!.push(r)
    }
    for (const arr of m.values()) arr.sort((a, b) => a.name.localeCompare(b.name))
    return m
  })()

  // Tree-ordered, depth-indented options for every parent picker on the tab.
  const treeOptions = useMemo(() => deptTreeOptions(departments), [departments])

  const handleCreate = async () => {
    const name = newName.trim()
    if (!name) return
    setError(null)
    try {
      await departmentApi.create(serverId, { name, parentId: newParent || null })
      setNewName('')
      setNewParent('')
      await onRefresh()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create')
    }
  }

  // Rename / set-head have no cross-field side effects, so we patch
  // locally and persist in the background (no refetch on success) —
  // exactly like RolesTab. A failure rolls back via onRefresh().
  const handleRename = async (id: string, name: string) => {
    const trimmed = name.trim()
    if (!trimmed) return
    onPatch(id, { name: trimmed })
    try {
      await departmentApi.update(serverId, id, { name: trimmed })
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to rename')
      await onRefresh()
    }
  }

  const handleSetHead = async (id: string, headUserId: string | null) => {
    onPatch(id, { headUserId })
    try {
      await departmentApi.update(serverId, id, { headUserId })
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to set head')
      await onRefresh()
    }
  }

  // Parent / reports-to drive the tree + chief grouping and are the
  // "exactly one of the two" pair — patch optimistically for instant
  // regroup, then reconcile silently against server truth.
  const handleSetParent = async (id: string, parentId: string | null) => {
    onPatch(id, { parentId })
    try {
      await departmentApi.update(serverId, id, { parentId })
      await onRefresh()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to move')
      await onRefresh()
    }
  }

  const handleSetReportsTo = async (id: string, reportsToUserId: string | null) => {
    onPatch(id, { reportsToUserId })
    try {
      await departmentApi.update(serverId, id, { reportsToUserId })
      await onRefresh()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to set reports-to')
      await onRefresh()
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (!(await dialog.confirm({ message: `Delete department "${name}"? Members are unassigned and child departments re-parent to root.`, tone: 'destructive', confirmLabel: 'Delete' }))) return
    setError(null)
    try {
      await departmentApi.delete(serverId, id)
      await onRefresh()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete')
    }
  }

  const toggleCollapsed = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  const clearSelection = () => setSelected(new Set())
  const selectAll = () => setSelected(new Set(departments.map((d) => d.id)))

  // Selected depts + every descendant — used to block them as a bulk-move
  // target (moving a dept under itself or its own child = a cycle the
  // server would reject anyway).
  const blockedAsTarget = useMemo(() => {
    const blocked = new Set<string>(selected)
    let frontier = [...selected]
    while (frontier.length) {
      const next: string[] = []
      for (const f of frontier) {
        for (const child of departments.filter((d) => d.parentId === f)) {
          if (!blocked.has(child.id)) { blocked.add(child.id); next.push(child.id) }
        }
      }
      frontier = next
    }
    return blocked
  }, [selected, departments])
  const bulkTargetIds = useMemo(
    () => new Set(departments.filter((d) => !blockedAsTarget.has(d.id)).map((d) => d.id)),
    [departments, blockedAsTarget],
  )

  const handleBulkMove = async () => {
    const target = bulkParent || null
    const ids = [...selected].filter((id) => id !== target)
    if (!ids.length) return
    setBulkBusy(true)
    setError(null)
    try {
      await Promise.all(ids.map((id) => departmentApi.update(serverId, id, { parentId: target })))
      clearSelection()
      setBulkParent('')
      await onRefresh()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to move selected departments')
    } finally {
      setBulkBusy(false)
    }
  }

  const handleBulkDelete = async () => {
    const ids = [...selected]
    if (!ids.length) return
    if (!(await dialog.confirm({ message: `Delete ${ids.length} department${ids.length === 1 ? '' : 's'}? Members are unassigned and child departments re-parent to root.`, tone: 'destructive', confirmLabel: 'Delete' }))) return
    setBulkBusy(true)
    setError(null)
    try {
      await Promise.all(ids.map((id) => departmentApi.delete(serverId, id)))
      clearSelection()
      await onRefresh()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete selected departments')
    } finally {
      setBulkBusy(false)
    }
  }

  const renderTier = (parentId: string | null, depth: number): ReactNode[] => {
    const tier = byParent.get(parentId) ?? []
    const out: ReactNode[] = []
    for (const dept of tier) {
      const hasChildren = (byParent.get(dept.id)?.length ?? 0) > 0
      const isCollapsed = collapsed.has(dept.id)
      out.push(
        <DepartmentRow
          key={dept.id}
          dept={dept}
          depth={depth}
          allDepartments={departments}
          members={members}
          hasChildren={hasChildren}
          isCollapsed={isCollapsed}
          selected={selected.has(dept.id)}
          onToggleSelect={() => toggleSelect(dept.id)}
          onToggleCollapsed={() => toggleCollapsed(dept.id)}
          onRename={(name) => handleRename(dept.id, name)}
          onSetParent={(pid) => handleSetParent(dept.id, pid)}
          onSetHead={(uid) => handleSetHead(dept.id, uid)}
          onSetReportsTo={(uid) => handleSetReportsTo(dept.id, uid)}
          onDelete={() => handleDelete(dept.id, dept.name)}
          onOpenPolicies={() => navigate(`/servers/${serverId}/departments/${dept.id}/policies`)}
        />,
      )
      if (!isCollapsed) {
        out.push(...renderTier(dept.id, depth + 1))
      }
    }
    return out
  }

  return (
    <div className="space-y-4">
      {error && <div className="text-sm bg-destructive/10 text-destructive rounded px-3 py-2">{error}</div>}

      <div className="flex items-center gap-2 bg-muted/40 rounded-lg p-3">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleCreate() }}
          placeholder="New department name"
          className="dept-input flex-1"
        />
        <select
          value={newParent}
          onChange={(e) => setNewParent(e.target.value)}
          className="dept-input"
          style={{ width: '180px' }}
        >
          <option value="">Root (top level)</option>
          {treeOptions.map((r) => (
            <option key={r.id} value={r.id}>{r.label}</option>
          ))}
        </select>
        <button
          onClick={handleCreate}
          disabled={!newName.trim()}
          className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 transition disabled:opacity-50"
        >
          <Plus size={14} className="inline mr-1" />Add
        </button>
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-primary/10 ring-1 ring-primary/30 rounded-lg p-3">
          <span className="text-sm font-semibold text-foreground shrink-0">{selected.size} selected</span>
          <div className="flex items-center gap-1.5 ml-1">
            <span className="text-xs text-muted-foreground">Move under</span>
            <select
              value={bulkParent}
              onChange={(e) => setBulkParent(e.target.value)}
              className="dept-input"
              style={{ width: '180px' }}
              disabled={bulkBusy}
            >
              <option value="">Root (top level)</option>
              {treeOptions.filter((o) => bulkTargetIds.has(o.id)).map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={handleBulkMove}
              disabled={bulkBusy}
              className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 transition disabled:opacity-50"
            >
              Move
            </button>
          </div>
          <button
            onClick={handleBulkDelete}
            disabled={bulkBusy}
            className="px-3 py-1.5 text-sm bg-destructive/15 text-destructive rounded hover:bg-destructive/25 transition disabled:opacity-50 flex items-center gap-1"
          >
            <Trash2 size={14} /> Delete
          </button>
          <button
            onClick={clearSelection}
            disabled={bulkBusy}
            className="ml-auto px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      )}

      {departments.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
          <button
            onClick={selected.size === departments.length ? clearSelection : selectAll}
            className="hover:text-foreground transition font-medium"
          >
            {selected.size === departments.length ? 'Clear selection' : 'Select all'}
          </button>
          <span>·</span>
          <span>{departments.length} {departments.length === 1 ? 'department' : 'departments'}</span>
        </div>
      )}

      {departments.length === 0 ? (
        <div className="text-muted-foreground text-sm py-4">No departments yet — add one above to get started.</div>
      ) : (
        <div className="space-y-4">
          {(() => {
            // Group top-level departments by the CHIEF they report to
            // (reportsToUserId) so the page mirrors the org chart: one
            // section per executive, listing the departments that roll up
            // to them. Departments with no chief land in "No chief assigned".
            const topLevel = byParent.get(null) ?? []
            const memberByUserId = new Map(members.map((m) => [m.userId, m.user]))
            const groups = new Map<string, { chiefId: string; chief: Member['user'] | null; depts: ServerDepartment[] }>()
            const unassigned: ServerDepartment[] = []
            for (const d of topLevel) {
              if (!d.reportsToUserId) {
                unassigned.push(d)
                continue
              }
              const key = d.reportsToUserId
              const entry = groups.get(key) ?? { chiefId: key, chief: memberByUserId.get(key) ?? null, depts: [] }
              entry.depts.push(d)
              groups.set(key, entry)
            }
            const ordered = Array.from(groups.values()).sort((a, b) =>
              displayLabel(a.chief).localeCompare(displayLabel(b.chief)),
            )

            const renderGroup = (heading: ReactNode, depts: ServerDepartment[]) => (
              <div className="rounded-xl ring-1 ring-border bg-card/40 overflow-hidden">
                <div className="px-4 py-2.5 bg-muted/40 border-b border-border flex items-center gap-2.5">
                  {heading}
                </div>
                <div className="space-y-1 py-2">
                  {depts.flatMap((d) => {
                    const hasChildren = (byParent.get(d.id)?.length ?? 0) > 0
                    const isCollapsed = collapsed.has(d.id)
                    const out: ReactNode[] = [
                      <DepartmentRow
                        key={d.id}
                        dept={d}
                        depth={0}
                        allDepartments={departments}
                        members={members}
                        hasChildren={hasChildren}
                        isCollapsed={isCollapsed}
                        selected={selected.has(d.id)}
                        onToggleSelect={() => toggleSelect(d.id)}
                        onToggleCollapsed={() => toggleCollapsed(d.id)}
                        onRename={(name) => handleRename(d.id, name)}
                        onSetParent={(pid) => handleSetParent(d.id, pid)}
                        onSetHead={(uid) => handleSetHead(d.id, uid)}
                        onSetReportsTo={(uid) => handleSetReportsTo(d.id, uid)}
                        onDelete={() => handleDelete(d.id, d.name)}
                        onOpenPolicies={() => navigate(`/servers/${serverId}/departments/${d.id}/policies`)}
                      />,
                    ]
                    if (!isCollapsed) out.push(...renderTier(d.id, 1))
                    return out
                  })}
                </div>
              </div>
            )

            return (
              <>
                {ordered.map((g) => (
                  <div key={g.chiefId}>
                    {renderGroup(
                      <>
                        <div className="w-7 h-7 rounded-full overflow-hidden bg-muted shrink-0">
                          {g.chief?.avatarUrl && (
                            <img src={mediaUrl(g.chief.avatarUrl)} alt="" className="w-full h-full object-cover" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-foreground truncate">
                            {displayLabel(g.chief) || 'Unknown chief'}
                          </div>
                          {g.chief?.position && (
                            <div className="text-[10px] uppercase tracking-wider text-primary font-bold">
                              {g.chief.position}
                            </div>
                          )}
                        </div>
                        <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">
                          {g.depts.length} {g.depts.length === 1 ? 'department' : 'departments'}
                        </span>
                      </>,
                      g.depts,
                    )}
                  </div>
                ))}
                {unassigned.length > 0 && (
                  <div>
                    {renderGroup(
                      <div className="text-sm font-semibold text-muted-foreground italic">
                        No chief assigned ({unassigned.length})
                      </div>,
                      unassigned,
                    )}
                  </div>
                )}
              </>
            )
          })()}
        </div>
      )}

      <style>{`.dept-input{background:var(--color-muted);color:var(--color-foreground);border-radius:6px;padding:6px 10px;font-size:13px;outline:none;border:1px solid transparent}.dept-input:focus{border-color:var(--color-primary)}`}</style>
    </div>
  )
}

function displayLabel(u: Member['user'] | null | undefined): string {
  if (!u) return ''
  const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
  return full || u.email || ''
}

function DepartmentRow({
  dept,
  depth,
  allDepartments,
  members,
  hasChildren,
  isCollapsed,
  selected,
  onToggleSelect,
  onToggleCollapsed,
  onRename,
  onSetParent,
  onSetHead,
  onSetReportsTo,
  onDelete,
  onOpenPolicies,
}: {
  dept: ServerDepartment
  depth: number
  allDepartments: ServerDepartment[]
  members: Member[]
  hasChildren: boolean
  isCollapsed: boolean
  selected: boolean
  onToggleSelect: () => void
  onToggleCollapsed: () => void
  onRename: (name: string) => void
  onSetParent: (parentId: string | null) => void
  onSetHead: (headUserId: string | null) => void
  onSetReportsTo: (reportsToUserId: string | null) => void
  onDelete: () => void
  onOpenPolicies: () => void
}) {
  const [editingName, setEditingName] = useState<string | null>(null)

  // Drop self + descendants from the parent dropdown so admin can't
  // pick a cycle. Server enforces too, but trimming locally is faster
  // feedback. Options are tree-ordered + indented (OSPROD-265).
  const parentOptions = (() => {
    const descendants = new Set<string>([dept.id])
    let frontier = [dept.id]
    while (frontier.length) {
      const next: string[] = []
      for (const f of frontier) {
        for (const child of allDepartments.filter((d) => d.parentId === f)) {
          if (!descendants.has(child.id)) {
            descendants.add(child.id)
            next.push(child.id)
          }
        }
      }
      frontier = next
    }
    return deptTreeOptions(allDepartments).filter((o) => !descendants.has(o.id))
  })()

  const parent = dept.parentId ? allDepartments.find((d) => d.id === dept.parentId) ?? null : null

  // Shared person options for both the "Reports to" (chief) and "Head"
  // pickers — every server member, searchable by name + role.
  const candidates = members
    .filter((m) => m.user)
    .slice()
    .sort((a, b) => displayLabel(a.user).localeCompare(displayLabel(b.user)))
    .map((m) => ({
      userId: m.userId,
      displayName: displayLabel(m.user),
      role: m.user.position ?? null,
      photo: m.user.avatarUrl ?? null,
    }))

  return (
    <div
      className={`flex items-center gap-2 px-2 py-2 rounded transition ${selected ? 'bg-primary/10' : 'hover:bg-accent/40'}`}
      style={{ paddingLeft: `${depth * 24 + 8}px` }}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggleSelect}
        className="shrink-0 w-3.5 h-3.5 cursor-pointer"
        style={{ accentColor: 'var(--color-primary)' }}
        title="Select for bulk action"
      />
      <button
        onClick={onToggleCollapsed}
        className={`w-5 h-5 flex items-center justify-center text-muted-foreground hover:text-foreground transition ${hasChildren ? '' : 'invisible'}`}
        title={isCollapsed ? 'Expand' : 'Collapse'}
      >
        {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
      </button>

      {/* OSPROD-265: explicit parent→child connector so a sub-department
          reads as nested, not as a root block that happens to be indented. */}
      {depth > 0 && (
        <CornerDownRight
          size={13}
          className="text-muted-foreground/70 shrink-0"
          aria-label={parent ? `Sub-department of ${parent.name}` : 'Sub-department'}
        />
      )}

      {editingName !== null ? (
        <input
          value={editingName}
          onChange={(e) => setEditingName(e.target.value)}
          onBlur={() => { if (editingName.trim() && editingName.trim() !== dept.name) onRename(editingName); setEditingName(null) }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { (e.target as HTMLInputElement).blur() }
            if (e.key === 'Escape') setEditingName(null)
          }}
          autoFocus
          className="dept-input flex-1 min-w-0"
        />
      ) : (
        <button
          onClick={() => setEditingName(dept.name)}
          className="text-sm text-foreground font-medium truncate hover:underline"
          title="Click to rename"
        >
          {dept.name}
        </button>
      )}

      {parent && (
        <span className="text-[10px] text-muted-foreground/70 truncate shrink-0" title={`Sub-department of ${parent.name} — inherits its permissions`}>
          in {parent.name}
        </span>
      )}

      <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70 shrink-0">
        {dept._count.members} {dept._count.members === 1 ? 'member' : 'members'}
      </span>

      {/* Reports to (chief) — primary control; drives the grouping above.
          OSPROD-265: disabled on sub-departments — they roll up (and inherit
          permissions) via their parent chain. Setting a chief here used to
          SILENTLY unparent the department; the server now rejects it too. */}
      <div className="shrink-0">
        <div className="text-[9px] uppercase tracking-wider text-muted-foreground/80 mb-0.5 pl-1">Reports to</div>
        <HeadSelect
          value={dept.reportsToUserId ?? null}
          candidates={candidates}
          onChange={(uid) => onSetReportsTo(uid)}
          placeholder="— chief —"
          width={200}
          disabled={!!dept.parentId}
          disabledTitle={`Sub-department of ${parent?.name ?? 'another department'} — it rolls up via its parent. Clear the parent to assign a chief.`}
        />
      </div>

      {/* Head — the department's day-to-day lead. */}
      <div className="shrink-0">
        <div className="text-[9px] uppercase tracking-wider text-muted-foreground/80 mb-0.5 pl-1">Head</div>
        <HeadSelect
          value={dept.headUserId ?? null}
          candidates={candidates}
          onChange={(uid) => onSetHead(uid)}
          placeholder="— head —"
          width={180}
        />
      </div>

      {/* Parent department — only for sub-departments; disabled when a
          chief is set (a department rolls up via exactly one of the two). */}
      <div className="shrink-0">
        <div className="text-[9px] uppercase tracking-wider text-muted-foreground/80 mb-0.5 pl-1">Parent dept</div>
        <select
          value={dept.parentId ?? ''}
          onChange={(e) => onSetParent(e.target.value || null)}
          className="dept-input"
          style={{ width: '150px' }}
          disabled={!!dept.reportsToUserId}
          title={dept.reportsToUserId ? 'Reports to a chief — clear it to set a parent department' : 'Parent department'}
        >
          <option value="">— none —</option>
          {parentOptions.map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
      </div>

      <div className="ml-auto flex items-center gap-1">
        <button
          onClick={onOpenPolicies}
          className="p-1.5 rounded text-muted-foreground hover:text-primary hover:bg-primary/10 transition"
          title="Communication policies"
        >
          <ShieldCheck size={14} />
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition"
          title="Delete"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  )
}
